import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { trackEvent } from 'helpers/Analytics';
import { Link } from 'react-router-dom';
import { Col, Grid, HelpBlock, Row } from 'react-bootstrap';
import Panel from 'components/Panel/Panel';
import styles from './ProjectForm.module.scss';
import { addCredits, createProject } from 'redux/modules/textProjects';
import useAuth from 'hooks/useAuth';
import ProjectMediaPanel from 'components/TextProjects/ProjectMediaPanel';
import ProjectDescriptionField from 'components/TextProjects/ProjectDescriptionField';
import ProjectRolesField from 'components/TextProjects/ProjectRolesField';
import Breadcrumbs from 'components/Shared/Breadcrumbs';
import { setSuccessMessage } from 'redux/modules/successMessage';
import {
  setProjectPublishFlash,
  toStorablePreviewUrl,
} from 'helpers/projectPublishFlash';
import Modal from 'components/Modal/Modal';

const CreateProject = () => {
  const dispatch = useDispatch();
  const { user } = useAuth();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [errors, setErrors] = useState({});
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [showEmailVerifyModal, setShowEmailVerifyModal] = useState(false);
  const [mediaPayload, setMediaPayload] = useState({
    file: null,
    thumbnail: null,
    crop: null,
    uploading: false,
    previewUrl: null,
  });

  const featuredRoles = (user?.featured_roles || []).slice(0, 5);

  const projectSelect = (state) => state.textProjects;
  const projectState = useSelector(projectSelect);

  const { creatingProject } = projectState;
  const busy = creatingProject || mediaPayload.uploading;
  const titleTrimmed = title.trim();
  const canCreate = Boolean(titleTrimmed) && !busy;

  const finishCreate = async (response, { creditsFailed = false } = {}) => {
    trackEvent(
      mediaPayload.file
        ? 'freelancer-creates-media-project'
        : 'freelancer-creates-text-project'
    );

    const hasMedia = Boolean(mediaPayload.file);
    if (hasMedia && response?.id) {
      const previewUrl = await toStorablePreviewUrl(mediaPayload.previewUrl);
      setProjectPublishFlash(response.id, {
        previewUrl,
        showBanner: true,
      });
      // Dedicated in-page banner replaces the global toast for media publishes.
      if (creditsFailed) {
        dispatch(
          setSuccessMessage(
            'Project created. Roles could not be saved — you can add them on the edit page.'
          )
        );
      }
    } else {
      dispatch(
        setSuccessMessage(
          creditsFailed
            ? 'Project created. Roles could not be saved — you can add them on the edit page.'
            : 'Your project has successfully been created'
        )
      );
    }

    window.location.href = response.links.main_relative;
  };

  useEffect(() => {
    if (!user.email_verified) {
      setShowEmailVerifyModal(true);
    }
  }, []);

  const handleRolesChange = (roles) => {
    setSelectedRoles(roles);
  };

  const buildCreateData = () => {
    const data = {
      title: titleTrimmed,
      description,
    };

    if (mediaPayload.file) {
      data.file = mediaPayload.file;
    }

    if (mediaPayload.thumbnail) {
      data.thumbnail = mediaPayload.thumbnail;
      if (mediaPayload.crop) {
        data.crop = mediaPayload.crop;
      }
    }

    return data;
  };

  const handleCreateProject = () => {
    if (!canCreate) {
      return;
    }

    setErrors({});

    dispatch(createProject(buildCreateData()))
      .then((response) => {
        if (!selectedRoles.length) {
          return finishCreate(response);
        }

        return dispatch(
          addCredits(
            response.id,
            user.id,
            selectedRoles.map((role) => role.value)
          )
        )
          .then(() => finishCreate(response))
          .catch((error) => {
            console.error('Error creating credits:', error);
            // Keep media payload — create already succeeded; redirect with warning
            return finishCreate(response, { creditsFailed: true });
          });
      })
      .catch((error) => {
        console.error('Error creating project:', error);
        // Upload reference is still valid on create failure — keep for retry
        setErrors(error.errors || {});
      });
  };

  return (
    <div className={styles.page}>
      <Grid>
        <Row>
          <Col xs={12}>
            <Breadcrumbs className={styles.breadcrumbs}>
              <Link to="/projects/manage">Manage projects</Link>
              Create project
            </Breadcrumbs>
            <Panel
              className={`${styles.container} box-shadow`}
              showSeparators={false}
            >
              <div className={styles.section}>
                <label className={styles.label} htmlFor="project-title">
                  Project title
                </label>
                <input
                  id="project-title"
                  type="text"
                  className={styles.input}
                  value={title}
                  placeholder="e.g. Brand film for a fintech launch"
                  onChange={(event) => setTitle(event.target.value)}
                  maxLength={140}
                  disabled={busy}
                />
                <p className={styles.helper}>
                  Clients see this in search and on your portfolio.
                </p>
                {errors?.title && (
                  <HelpBlock className={styles.error}>
                    {errors?.title[0]}
                  </HelpBlock>
                )}
              </div>

              <hr className={styles.divider} />

              <div className={styles.section}>
                <ProjectMediaPanel
                  mode="create"
                  disabled={busy}
                  projectTitle={title}
                  projectDescription={description}
                  firstRole={
                    (selectedRoles[0] &&
                      (selectedRoles[0].label || selectedRoles[0].value)) ||
                    ''
                  }
                  onChange={setMediaPayload}
                />
                {errors?.file && (
                  <HelpBlock className={styles.error}>
                    {errors.file[0]}
                  </HelpBlock>
                )}
                {errors?.project_id && (
                  <HelpBlock className={styles.error}>
                    {errors.project_id[0]}
                  </HelpBlock>
                )}
              </div>

              <div className={styles.section}>
                <ProjectDescriptionField
                  value={description}
                  onChange={setDescription}
                  disabled={busy}
                  error={errors?.description?.[0]}
                />
              </div>

              <div className={styles.section}>
                <ProjectRolesField
                  key={JSON.stringify(featuredRoles)}
                  maxValues={5}
                  existing={featuredRoles}
                  onSelect={handleRolesChange}
                  disabled={busy}
                  error={errors?.roles?.[0]}
                />
              </div>

              <div className={styles.buttonContainer}>
                <Link
                  to="/projects/manage"
                  className={`${styles.cancelButton}${
                    busy ? ` ${styles.cancelDisabled}` : ''
                  }`}
                  onClick={(event) => {
                    if (busy) event.preventDefault();
                  }}
                >
                  Cancel
                </Link>
                <button
                  type="button"
                  className={styles.primaryButton}
                  onClick={() => {
                    if (!user.email_verified) {
                      setShowEmailVerifyModal(true);
                      return;
                    }
                    handleCreateProject();
                  }}
                  disabled={!canCreate}
                >
                  {mediaPayload.uploading
                    ? 'Uploading media…'
                    : creatingProject
                    ? 'Creating…'
                    : 'Create project'}
                </button>
              </div>
            </Panel>
          </Col>
        </Row>
      </Grid>
      <Modal
        show={showEmailVerifyModal}
        onExit={() => setShowEmailVerifyModal(false)}
        closeButton
      >
        <div className="p++">
          <h3 className="lato-bold text-center mb">Activate your Email</h3>
          <p>
            Before you can add a new project, you need to activate your account.
          </p>
          <p>
            Please check your email for an email from us with an activation
            button and click it.
          </p>
          <p>
            Please check your spam/junk folder in case it has ended up in there.
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default CreateProject;
